import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Conversion, ApiResponse } from '../types';
import clsx from 'clsx';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  paid: 'bg-blue-100 text-blue-800',
  revision: 'bg-purple-100 text-purple-800',
};

export default function Conversions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const status = searchParams.get('status') || '';
  const campaignId = searchParams.get('campaignId') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const { data, isLoading } = useQuery({
    queryKey: ['conversions', { status, campaignId, page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (campaignId) params.set('campaignId', campaignId);
      params.set('page', page.toString());

      const response = await api.get<ApiResponse<Conversion[]>>(`/conversions?${params}`);
      return response.data;
    },
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns-list'],
    queryFn: async () => {
      const response = await api.get('/campaigns?limit=100');
      return response.data.data;
    },
  });

  const bulkUpdate = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      await api.patch('/conversions/bulk-status', { ids, status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversions'] });
      toast.success('Conversions updated');
      setSelectedIds([]);
    },
    onError: () => {
      toast.error('Failed to update conversions');
    },
  });

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(data?.data?.map((c) => c.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Conversions</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Track and manage affiliate conversions across all campaigns.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <select
          value={status}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) params.set('status', e.target.value);
            else params.delete('status');
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="paid">Paid</option>
          <option value="revision">Revision</option>
        </select>

        <select
          value={campaignId}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) params.set('campaignId', e.target.value);
            else params.delete('campaignId');
            params.set('page', '1');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Campaigns</option>
          {campaigns?.map((campaign: any) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="mt-4 flex items-center gap-4 bg-[var(--bg-secondary)] p-3 rounded-lg">
          <span className="text-sm text-[var(--text-secondary)]">
            {selectedIds.length} selected
          </span>
          <button
            onClick={() => bulkUpdate.mutate({ ids: selectedIds, status: 'approved' })}
            className="btn btn-sm bg-green-600 text-white hover:bg-green-700"
            disabled={bulkUpdate.isPending}
          >
            Approve
          </button>
          <button
            onClick={() => bulkUpdate.mutate({ ids: selectedIds, status: 'rejected' })}
            className="btn btn-sm bg-red-600 text-white hover:bg-red-700"
            disabled={bulkUpdate.isPending}
          >
            Reject
          </button>
          <button
            onClick={() => bulkUpdate.mutate({ ids: selectedIds, status: 'revision' })}
            className="btn btn-sm bg-purple-600 text-white hover:bg-purple-700"
            disabled={bulkUpdate.isPending}
          >
            Mark for Review
          </button>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3">
                <input
                  type="checkbox"
                  checked={selectedIds.length === data?.data?.length && data?.data?.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-[var(--border-color)]"
                />
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Campaign
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Affiliate
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Goal
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Amount
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Commission
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Fraud Score
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-[var(--text-muted)]">
                  Loading...
                </td>
              </tr>
            ) : data?.data?.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-[var(--text-muted)]">
                  No conversions found
                </td>
              </tr>
            ) : (
              data?.data?.map((conversion) => (
                <tr key={conversion.id} className={clsx(conversion.fraudScore > 50 && 'bg-red-50')}>
                  <td className="py-4 pl-4 pr-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(conversion.id)}
                      onChange={() => handleSelect(conversion.id)}
                      className="h-4 w-4 rounded border-[var(--border-color)]"
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">
                      {conversion.campaign?.name}
                    </div>
                    <div className="text-[var(--text-muted)] text-xs">
                      {conversion.campaign?.slug}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">
                      {conversion.affiliate?.name}
                    </div>
                    <code className="text-xs text-[var(--text-muted)]">
                      {conversion.affiliate?.referralCode}
                    </code>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {conversion.goal?.name || conversion.goal?.value || '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)] font-medium">
                    {conversion.currency} {conversion.amount.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-green-600 font-medium">
                    {conversion.commissionCurrency} {conversion.commission.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <FraudScoreBadge score={conversion.fraudScore} flags={conversion.fraudFlags} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={clsx(
                        'badge',
                        statusColors[conversion.status as keyof typeof statusColors]
                      )}
                    >
                      {conversion.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                    {new Date(conversion.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= data.pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FraudScoreBadge({ score, flags }: { score: number; flags?: string[] }) {
  let color = 'bg-green-100 text-green-800';
  if (score >= 70) {
    color = 'bg-red-100 text-red-800';
  } else if (score >= 40) {
    color = 'bg-yellow-100 text-yellow-800';
  }

  return (
    <div className="flex items-center gap-2">
      <span className={clsx('badge', color)}>{score}</span>
      {flags && flags.length > 0 && (
        <span title={flags.join(', ')} className="text-red-500 cursor-help">
          ({flags.length} flags)
        </span>
      )}
    </div>
  );
}
